import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Home, Sparkles, BookOpen, ArrowRight, Download } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';

const IOS_LINK = 'https://apps.apple.com/us/app/couplein/id6749887412';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=dev.expo.couplein&pcampaignid=web_share';

export default function NotFound() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/60 via-white to-purple-50/40 flex flex-col justify-between text-gray-900">
      <SEO
        title="404 - Page Not Found"
        description="The page you are looking for does not exist or has been moved. Explore CoupleIn relationship quizzes, features, and blog articles."
        path="/404"
        noindex={true}
      />

      <Navbar />

      <main className="flex-1 flex items-center justify-center px-4 pt-28 pb-16">
        <div className="max-w-2xl mx-auto text-center flex flex-col items-center">
          
          {/* Animated 404 Hero Illustration & Centered Tag */}
          <div className="flex flex-col items-center justify-center mb-6 w-full">
            <div className="relative mb-5">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, type: 'spring' }}
                className="w-36 h-36 sm:w-44 sm:h-44 rounded-full bg-gradient-to-tr from-pink-500 via-rose-400 to-amber-300 flex items-center justify-center shadow-2xl shadow-pink-500/30 relative mx-auto"
              >
                <Heart className="w-18 h-18 sm:w-22 sm:h-22 text-white fill-white animate-pulse" />
                
                {/* Floating Sparkle Badge */}
                <motion.div
                  animate={{ y: [-4, 4, -4], rotate: [0, 10, 0] }}
                  transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                  className="absolute -top-1 -right-1 bg-amber-400 text-white p-2.5 rounded-2xl shadow-md"
                >
                  <Sparkles className="w-5 h-5 fill-current" />
                </motion.div>
              </motion.div>
            </div>

            {/* Error Code Tag (Centered under heart) */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-pink-100/90 text-pink-700 px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold tracking-wide uppercase shadow-xs"
            >
              <span>Error 404 • Lost in Love</span>
            </motion.div>
          </div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl sm:text-5xl font-extrabold text-gray-900 tracking-tight leading-tight mb-4"
          >
            Oops! This page took a little detour.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-base sm:text-lg text-gray-600 max-w-md mx-auto mb-8 leading-relaxed"
          >
            We couldn't find the page you were looking for, but you can always find your way back to what matters most.
          </motion.p>

          {/* Action CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3.5 mb-12 w-full"
          >
            <Link
              to="/"
              data-testid="404-home-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-700 text-white font-semibold px-6 py-3.5 rounded-2xl transition-all hover:scale-105 active:scale-95 shadow-lg shadow-pink-600/20 text-sm"
            >
              <Home className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>

            <Link
              to="/quiz/love-language"
              data-testid="404-quiz-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-pink-50/60 text-gray-800 hover:text-pink-600 font-semibold px-6 py-3.5 rounded-2xl border border-gray-200 shadow-sm transition-all hover:scale-105 active:scale-95 text-sm"
            >
              <Sparkles className="w-4 h-4 text-pink-500" />
              <span>Take Free Love Quiz</span>
            </Link>

            <Link
              to="/blog"
              data-testid="404-blog-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-semibold px-6 py-3.5 rounded-2xl border border-gray-200 shadow-sm transition-all text-sm"
            >
              <BookOpen className="w-4 h-4 text-gray-500" />
              <span>Relationship Blog</span>
            </Link>
          </motion.div>

          {/* Quick Helpful Links Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white/80 backdrop-blur-md rounded-3xl p-6 sm:p-8 border border-gray-200/80 shadow-sm max-w-xl mx-auto w-full"
          >
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 text-center">
              Popular Destinations
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              <Link
                to="/never-forget-anything"
                className="p-3 rounded-xl bg-gray-50/80 hover:bg-pink-50/70 border border-gray-100 hover:border-pink-200 transition-colors group flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-pink-600">Shared Calendar</p>
                  <p className="text-xs text-gray-500">Never miss important dates</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-pink-500 group-hover:translate-x-0.5 transition-transform" />
              </Link>

              <Link
                to="/solve-fights-in-5-minutes"
                className="p-3 rounded-xl bg-gray-50/80 hover:bg-purple-50/70 border border-gray-100 hover:border-purple-200 transition-colors group flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-purple-600">Guided Resolution</p>
                  <p className="text-xs text-gray-500">Stop circular arguments</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-purple-500 group-hover:translate-x-0.5 transition-transform" />
              </Link>

              <Link
                to="/love-with-intentionality"
                className="p-3 rounded-xl bg-gray-50/80 hover:bg-rose-50/70 border border-gray-100 hover:border-rose-200 transition-colors group flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-rose-600">Brownie Points</p>
                  <p className="text-xs text-gray-500">Celebrate daily kindness</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-rose-500 group-hover:translate-x-0.5 transition-transform" />
              </Link>

              <Link
                to="/quiz/attachment-style"
                className="p-3 rounded-xl bg-gray-50/80 hover:bg-amber-50/70 border border-gray-100 hover:border-amber-200 transition-colors group flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-amber-600">Attachment Quiz</p>
                  <p className="text-xs text-gray-500">Understand your bond</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>

            {/* App Downloads */}
            <div className="mt-6 pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
              <span className="font-medium">Get CoupleIn on your phone:</span>
              <div className="flex items-center gap-2">
                <a
                  href={IOS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-1.5"
                >
                  <Download className="w-3 h-3" />
                  <span>iOS App</span>
                </a>
                <a
                  href={ANDROID_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-1.5"
                >
                  <Download className="w-3 h-3" />
                  <span>Android</span>
                </a>
              </div>
            </div>
          </motion.div>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-6 text-center text-xs text-gray-400 bg-white/60">
        <p>© {new Date().getFullYear()} Couple In LTD. All rights reserved.</p>
      </footer>
    </div>
  );
}
